/**
 * Returns whether the provided parameter is a URL or not
 * @param {string} url
 * @param {Object} options
 * @param {boolean} options.strict Disallow links without origins such as mailto: and tel: links
 * @param {boolean} options.ssl Also check if the URL is https
 * @param {string[]} options.allowed_protocols Additional protocols to allow
 * @returns {boolean}
 */

export default function isValidUrl(url, options) {
  const config = {
    strict: true,
    ssl: false,
    allowedProtocols: allowedProtocols,
    requireTld: false,
    ...options
  }
  const secure_protocols = ["https:", "ftps:"]
  const unsecure_protocols = ["http:", "ftp:"]
  const tldPattern = /\.[a-z]{2,}$/

  try {
    let urlInstance

    // All strict cases handled in this if block
    if (config.strict) {
      urlInstance = new URL(url)

      if (config.requireTld) {
        const isLocalhost = urlInstance.hostname === "localhost"
        const hasValidTld = tldPattern.test(urlInstance.hostname)

        if (!isLocalhost && !hasValidTld) return false
      }

      if (config.ssl) return secure_protocols.includes(urlInstance.protocol)
      return [...secure_protocols, ...unsecure_protocols].includes(
        urlInstance.protocol
      )
    }

    const URLWithProtocol = /(^\w{3,5}:)/.test(url) ? url : `http://${url}`
    urlInstance = new URL(URLWithProtocol)

    if (config.requireTld) {
      const isLocalhost = urlInstance.hostname === "localhost"
      const hasValidTld = tldPattern.test(urlInstance.hostname)

      if (!isLocalhost && !hasValidTld) return false
    }

    if (config.ssl) return secure_protocols.includes(urlInstance.protocol)

    return config.allowedProtocols.includes(urlInstance.protocol)
  } catch {
    return false
  }
}

export const allowedProtocols = [
  "mailto:",
  "tel:",
  "ftp:",
  "ftps:",
  "http:",
  "https:"
]

export function tryAddProtocol(url) {
  if (!url) return ""

  const encodedUrl = encodeURI(url)
  const startsWithAllowedProtocol = allowedProtocols.some(protocol =>
    encodedUrl?.toLowerCase().startsWith(protocol)
  )

  if (startsWithAllowedProtocol) return encodedUrl

  return `https://${encodedUrl}`
}
